import React, { useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import DeleteIcon from '@material-ui/icons/Delete';
import CircularProgress from '@material-ui/core/CircularProgress';
import { QeeColorButtonSecondary } from './qeeColorButton';
import { ORD_TYPE_SYMBOLS, ORD_TYPES, ORD_STATE_COLORS, ORD_STATES } from '../models/ordConfig';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        cursor: 'pointer',
        maxWidth: 800,
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            padding: theme.spacing(1.5),
        },
    },
    contentRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexGrow: 1,
    },
    iconColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1),
        },
        flex: 1,
    },
    documentIcon: {
        fontSize: '8rem', // Set the icon size here
        color: theme.palette.primary.main,
    },
    typeText: {
        fontSize: 18,
        color: theme.palette.text.secondary,
        textAlign: 'center',
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            fontSize: 12,
            marginTop: theme.spacing(1),
        },
    },
    title: {
        fontSize: 16,
        fontWeight: 600,
        color: theme.palette.primary.main,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: 'left',
        flexGrow: 1,
        marginLeft: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            fontSize: 14,
            marginLeft: theme.spacing(1),
        },
    },
    subtitle: {
        fontSize: 14,
        color: theme.palette.text.secondary,
        textAlign: 'left',
        flexGrow: 1,
        marginLeft: theme.spacing(0),
        [theme.breakpoints.down('sm')]: {
            fontSize: 12,
            marginLeft: theme.spacing(1),
        },
    },
    stateText: {
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0.5, 1),
        color: theme.palette.common.white,
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(0.3, 0.8),
            marginTop: theme.spacing(1),
        },
    },
    buttonContainer: {
        marginTop: theme.spacing(1),
        display: 'flex',
        justifyContent: 'flex-end',
    },
    actionButton: {
        marginLeft: theme.spacing(1),
        height: '36px',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            marginLeft: theme.spacing(0.5),
            height: '32px',
        },
    },
    icon: {
        color: theme.palette.primary.main,
        [theme.breakpoints.down('sm')]: {
            fontSize: 20,
        },
    },
}));

const OrdCard = ({ ord, onClick, onStateChange, onDelete }) => {
    const classes = useStyles();
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const [loadingState, setLoadingState] = useState(null);

    const handleStateChange = (state) => {
        setIsLoading(true);
        setLoadingState(state);
        onStateChange(ord, state)
            .finally(() => {
                setIsLoading(false);
                setLoadingState(null);
            })
            .catch((error) => console.error('Failed to update Ord state', error));
    };

    // Get the icon and adjust the size
    const ordTypeIcon = React.cloneElement(ORD_TYPE_SYMBOLS[ord.ord_type] || <OpenInNewIcon />, {
        className: classes.documentIcon,
    });
    const ordTypeText = ORD_TYPES[ord.ord_type] || 'Unbekannt';
    //const ordStateText = ORD_STATES[ord.ord_type][ord.ord_state] || 'Unbekannt';
	const ordType = ORD_STATES[ord.ordType] ? ord.ordType : 'default';
	const ordStateText = ORD_STATES[ordType][ord.ord_state] || 'Unbekannt';
    const ordStateColor = ORD_STATE_COLORS[ord.ord_state] || '#000';

    // Format the start and end dates
    const startDate = ord.contract?.startDate ? new Date(ord.contract.startDate).toLocaleDateString() : 'N/A';
    const endDate = ord.contract?.endDate ? new Date(ord.contract.endDate).toLocaleDateString() : 'N/A';

    return (
        <Card className={classes.root}>
            <Box className={classes.contentRow}>
                <Box className={classes.iconColumn}>
                    {ordTypeIcon}
                    <Typography className={classes.typeText}>{ordTypeText}</Typography>
                    <Box className={classes.stateText} style={{ backgroundColor: ordStateColor }}>
                        {ordStateText}
                    </Box>
                </Box>

                <CardContent className={classes.title}>
                    <Typography
                        variant="h6"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClick();
                        }}
                    >
                        {ord.art_name} ({ord.art_id})
                    </Typography>
                    <Typography className={classes.subtitle}>
                        {ord.ord_usr_name}
                    </Typography>
					{ord.ord_state >= 300 &&	
						<Typography className={classes.subtitle}>
							Laufzeit: {startDate} - {endDate}
						</Typography>
					}
                </CardContent>
            </Box>

            <CardActions className={classes.buttonContainer}>
                <QeeColorButtonSecondary
                    className={classes.actionButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(ord);
                    }}
                    disabled={isLoading}
                >
                    {isLoading && loadingState === 'delete' ? <CircularProgress size={24} /> : <DeleteIcon className={classes.icon} />}
                </QeeColorButtonSecondary>
                <QeeColorButtonSecondary
                    className={classes.actionButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                    }}
                    disabled={isLoading}
                >
                    <OpenInNewIcon className={classes.icon} />
                </QeeColorButtonSecondary>
            </CardActions>
        </Card>
    );
};

export default OrdCard;
